import { apiClient } from '@/api/client';
import type { ApiResponseWrapper } from '@/types/api.types';

export type HrDocumentType =
  | 'offer'
  | 'appointment'
  | 'confirmation'
  | 'warning'
  | 'promotion'
  | 'salary_revision'
  | 'termination'
  | 'resignation_acceptance'
  | 'experience'
  | 'relieving';

export type TemplateVersionStatus = 'draft' | 'pending_approval' | 'approved' | 'retired';
export type TemplateBlockKind = 'heading' | 'paragraph' | 'bullets' | 'spacer' | 'signature';
export type SignerRole = 'employee' | 'hr' | 'manager' | 'named';
export type HrDocumentStatus =
  | 'generated'
  | 'issued'
  | 'partially_signed'
  | 'completed'
  | 'refused'
  | 'expired'
  | 'voided';

export interface TemplateBlock {
  kind: TemplateBlockKind;
  text?: string;
  items?: string[];
  signerOrder?: number;
}

export interface TemplateSigner {
  order: number;
  role: SignerRole;
  userId?: string;
  label: string;
}

export interface TemplateVersion {
  _id: string;
  templateId: string;
  type: HrDocumentType;
  version: number;
  status: TemplateVersionStatus;
  title: string;
  blocks: TemplateBlock[];
  signers: TemplateSigner[];
  variables: string[];
  requiresSignature: boolean;
  expiresAfterDays: number;
  contentSha256?: string;
  createdBy: string;
  submittedBy?: string;
  approvedBy?: string;
  approvedAt?: string;
  updatedAt?: string;
}

export interface VersionSummary {
  _id: string;
  version: number;
  status: TemplateVersionStatus;
  title: string;
  updatedAt?: string;
}

export interface HrDocumentTemplate {
  _id: string;
  companyId: string;
  type: HrDocumentType;
  name: string;
  archived: boolean;
  currentVersionId?: string;
  currentVersion: VersionSummary | null;
  openVersion: VersionSummary | null;
}

export interface VariableDefinition {
  key: string;
  label: string;
  compensation: boolean;
  optional: boolean;
}

export interface VersionContent {
  title: string;
  blocks: TemplateBlock[];
  signers: TemplateSigner[];
  requiresSignature: boolean;
  expiresAfterDays: number;
}

export interface EnvelopeSignerView {
  order: number;
  userId: string;
  role: SignerRole;
  label: string;
  displayName: string;
  status: 'pending' | 'viewed' | 'signed' | 'refused';
  viewedAt?: string;
  signedAt?: string;
  refusedAt?: string;
  refusedReason?: string;
}

export interface HrDocument {
  _id: string;
  companyId: string;
  userId: string;
  templateId: string;
  templateVersionId: string;
  type: HrDocumentType;
  title: string;
  status: HrDocumentStatus;
  visibility: 'general' | 'hr_only';
  containsCompensation: boolean;
  resolvedVariables: Record<string, string>;
  artifact: { sha256: string; size: number; renderedAt: string };
  completedArtifact?: { sha256: string; size: number; renderedAt: string };
  issuedAt?: string;
  completedAt?: string;
  voidedAt?: string;
  voidReason?: string;
  createdAt: string;
  employee?: { name: string; employeeId?: string } | null;
  envelope?: {
    _id: string;
    status: string;
    expiresAt: string;
    documentSha256: string;
    signers: EnvelopeSignerView[];
    events: Array<{ type: string; at: string }>;
  } | null;
}

export interface HrDocumentPage {
  data: HrDocument[];
  total: number;
  page: number;
  limit: number;
}

export interface VerificationResult {
  valid: boolean;
  checks: Array<{ check: string; valid: boolean }>;
  verifiedAt: string;
}

export interface InboxEntry {
  _id: string;
  companyId: string;
  type: HrDocumentType;
  title: string;
  status: HrDocumentStatus;
  issuedAt?: string;
  completedAt?: string;
  documentSha256: string;
  hasSignedCopy: boolean;
  mySigner: { order: number; label: string; status: string; signedAt?: string } | null;
  awaitingMySignature: boolean;
  expiresAt?: string;
}

export interface SigningConsent {
  version: string;
  text: string;
}

const base = (companyId: string): string => `/companies/${companyId}/hr/documents`;
const unwrap = <T>(data: ApiResponseWrapper<T>): T => data.data;

export const DOCUMENT_TYPE_LABELS: Record<HrDocumentType, string> = {
  offer: 'Offer letter',
  appointment: 'Appointment letter',
  confirmation: 'Confirmation letter',
  warning: 'Warning letter',
  promotion: 'Promotion letter',
  salary_revision: 'Salary revision letter',
  termination: 'Termination letter',
  resignation_acceptance: 'Resignation acceptance',
  experience: 'Experience letter',
  relieving: 'Relieving letter',
};

export async function listTemplates(
  companyId: string,
  includeArchived = false
): Promise<HrDocumentTemplate[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<HrDocumentTemplate[]>>(
    `${base(companyId)}/templates`,
    { params: includeArchived ? { includeArchived: 'true' } : undefined }
  );
  return unwrap(data);
}

export async function createTemplate(
  companyId: string,
  input: { type: HrDocumentType; name: string }
): Promise<{ template: HrDocumentTemplate; version: TemplateVersion }> {
  const { data } = await apiClient.post<
    ApiResponseWrapper<{ template: HrDocumentTemplate; version: TemplateVersion }>
  >(`${base(companyId)}/templates`, input);
  return unwrap(data);
}

export async function getTemplate(
  companyId: string,
  templateId: string
): Promise<{ template: HrDocumentTemplate; versions: VersionSummary[] }> {
  const { data } = await apiClient.get<
    ApiResponseWrapper<{ template: HrDocumentTemplate; versions: VersionSummary[] }>
  >(`${base(companyId)}/templates/${templateId}`);
  return unwrap(data);
}

export async function setTemplateArchived(
  companyId: string,
  templateId: string,
  archived: boolean
): Promise<void> {
  await apiClient.post(
    `${base(companyId)}/templates/${templateId}/${archived ? 'archive' : 'restore'}`
  );
}

export async function createDraftVersion(
  companyId: string,
  templateId: string
): Promise<TemplateVersion> {
  const { data } = await apiClient.post<ApiResponseWrapper<TemplateVersion>>(
    `${base(companyId)}/templates/${templateId}/versions`
  );
  return unwrap(data);
}

export async function getVersion(companyId: string, versionId: string): Promise<TemplateVersion> {
  const { data } = await apiClient.get<ApiResponseWrapper<TemplateVersion>>(
    `${base(companyId)}/versions/${versionId}`
  );
  return unwrap(data);
}

export async function saveDraft(
  companyId: string,
  versionId: string,
  content: VersionContent
): Promise<TemplateVersion> {
  const { data } = await apiClient.put<ApiResponseWrapper<TemplateVersion>>(
    `${base(companyId)}/versions/${versionId}`,
    content
  );
  return unwrap(data);
}

type VersionCommand = 'submit' | 'approve' | 'discard';

export async function runVersionCommand(
  companyId: string,
  versionId: string,
  command: VersionCommand
): Promise<void> {
  await apiClient.post(`${base(companyId)}/versions/${versionId}/${command}`);
}

export async function returnVersionToDraft(
  companyId: string,
  versionId: string,
  reason: string
): Promise<void> {
  await apiClient.post(`${base(companyId)}/versions/${versionId}/return`, { reason });
}

export async function previewVersion(
  companyId: string,
  versionId: string,
  userId?: string
): Promise<string> {
  const { data } = await apiClient.post<ApiResponseWrapper<{ html: string }>>(
    `${base(companyId)}/versions/${versionId}/preview`,
    userId ? { userId } : {}
  );
  return unwrap(data).html;
}

export async function listVariables(
  companyId: string,
  type: HrDocumentType
): Promise<VariableDefinition[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<VariableDefinition[]>>(
    `${base(companyId)}/variables`,
    { params: { type } }
  );
  return unwrap(data);
}

export async function listDocuments(
  companyId: string,
  params: { userId?: string; status?: HrDocumentStatus; page?: number; limit?: number } = {}
): Promise<HrDocumentPage> {
  const { data } = await apiClient.get<ApiResponseWrapper<HrDocumentPage>>(base(companyId), {
    params,
  });
  return unwrap(data);
}

export async function generateDocument(
  companyId: string,
  input: { templateId: string; userId: string; requestId: string }
): Promise<HrDocument> {
  const { data } = await apiClient.post<ApiResponseWrapper<HrDocument>>(base(companyId), input);
  return unwrap(data);
}

export async function getDocument(companyId: string, documentId: string): Promise<HrDocument> {
  const { data } = await apiClient.get<ApiResponseWrapper<HrDocument>>(
    `${base(companyId)}/${documentId}`
  );
  return unwrap(data);
}

export async function issueDocument(companyId: string, documentId: string): Promise<HrDocument> {
  const { data } = await apiClient.post<ApiResponseWrapper<HrDocument>>(
    `${base(companyId)}/${documentId}/issue`
  );
  return unwrap(data);
}

export async function voidDocument(
  companyId: string,
  documentId: string,
  reason: string
): Promise<HrDocument> {
  const { data } = await apiClient.post<ApiResponseWrapper<HrDocument>>(
    `${base(companyId)}/${documentId}/void`,
    { reason }
  );
  return unwrap(data);
}

export async function verifyDocument(
  companyId: string,
  documentId: string
): Promise<VerificationResult> {
  const { data } = await apiClient.get<ApiResponseWrapper<VerificationResult>>(
    `${base(companyId)}/${documentId}/verify`
  );
  return unwrap(data);
}

export async function downloadDocumentPdf(
  companyId: string,
  documentId: string,
  which: 'original' | 'completed'
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`${base(companyId)}/${documentId}/artifact`, {
    params: { which },
    responseType: 'blob',
  });
  return data;
}

export async function listMyLetters(): Promise<InboxEntry[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<InboxEntry[]>>('/me/hr-documents');
  return unwrap(data);
}

export async function getSigningConsent(): Promise<SigningConsent> {
  const { data } = await apiClient.get<ApiResponseWrapper<SigningConsent>>(
    '/me/hr-documents/consent'
  );
  return unwrap(data);
}

export async function downloadMyLetterPdf(
  documentId: string,
  which: 'original' | 'completed'
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/me/hr-documents/${documentId}/artifact`, {
    params: { which },
    responseType: 'blob',
  });
  return data;
}

export async function markLetterViewed(documentId: string): Promise<void> {
  await apiClient.post(`/me/hr-documents/${documentId}/view`);
}

export async function signLetter(
  documentId: string,
  input: {
    signatureImage: string;
    consentAccepted: true;
    consentVersion: string;
    documentSha256: string;
  }
): Promise<{ status: string }> {
  const { data } = await apiClient.post<ApiResponseWrapper<{ status: string }>>(
    `/me/hr-documents/${documentId}/sign`,
    input
  );
  return unwrap(data);
}

export async function refuseLetter(documentId: string, reason: string): Promise<void> {
  await apiClient.post(`/me/hr-documents/${documentId}/refuse`, { reason });
}
